/**
 * Comunicazione diretta con gli impedenziometri BioSmart tramite Web Serial.
 *
 * La pagina Sibilla può essere pubblicata su un server remoto: Web Serial
 * accede comunque alla porta USB/Bluetooth del computer del professionista,
 * dopo una selezione esplicita nel browser.
 */

const SUPPORTED_MODELS = ["EX-118BT", "EX-117BT", "EX-316BT", "TS-515-N", "IMP-0120BT"];
const SERIAL_OPTIONS = {
  baudRate: 115200,
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  bufferSize: 1024,
  flowControl: "none"
};

const delay = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

export const DEFAULT_ACQUISITION_PROFILE = 'multifrequency';
export const BIA_ACQUISITION_PROFILES = Object.freeze({
  multifrequency: Object.freeze([5, 50, 100]),
  classic: Object.freeze([50, 100])
});
const MEASUREMENT_COMMANDS = Object.freeze({ 5: '[m,0]', 50: '[m,3]', 100: '[m,5]' });

function profileFrequencies(profile) {
  if (!Object.prototype.hasOwnProperty.call(BIA_ACQUISITION_PROFILES, profile)) {
    throw new Error('Profilo di acquisizione non valido. Scegli multifrequenza o classico.');
  }
  return BIA_ACQUISITION_PROFILES[profile];
}

function parseNumber(value, label) {
  const parsed = Number(String(value).trim());
  if (!String(value).trim() || !Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`Il dispositivo ha restituito un valore ${label} non valido.`);
  }
  return parsed;
}

function parseMeasurementLine(raw, frequency) {
  const parts = String(raw).split(':');
  if (parts.length !== 4) {
    throw new Error(`Risposta ${frequency} kHz incompleta: ripeti la misurazione.`);
  }

  const impedance = parseNumber(parts[0], `Z ${frequency} kHz`);
  const phase = parseNumber(parts[1], `fase ${frequency} kHz`);
  // Ordine verificato in GedipBioImp2: Z:fase:R:Xc. Non correggere
  // silenziosamente i valori grezzi scambiando resistenza e reattanza.
  const resistance = parseNumber(parts[2], `R ${frequency} kHz`);
  const reactance = parseNumber(parts[3], `Xc ${frequency} kHz`);

  return { impedance, phase, resistance, reactance };
}

export function toApiMeasurement(raw) {
  const frequencies = profileFrequencies(raw.acquisitionProfile ?? DEFAULT_ACQUISITION_PROFILE);
  const fields = { acquisitionProfile: raw.acquisitionProfile ?? DEFAULT_ACQUISITION_PROFILE };
  for (const frequency of [5, 50, 100]) {
    for (const [field, prefix] of [['impedance', 'imp'], ['phase', 'ang'], ['resistance', 'res'], ['reactance', 'rea']]) {
      // Explicit null clears values from a previous acquisition when merged.
      // Never fabricate 5 kHz data for the classic profile.
      fields[`${field}${frequency}`] = frequencies.includes(frequency)
        ? parseNumber(raw[`${prefix}_${frequency}`], `${field} ${frequency} kHz`)
        : null;
    }
  }
  return fields;
}

export class BIASerial {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.onLog = null;
    this.onTransportError = null;
    this._readTask = null;
    this._readBuffer = '';
    this._pendingLine = null;
    this._closing = false;
    this._measuring = false;
    this._commandInFlight = false;
    this._disconnectHandler = event => {
      if (event.target !== this.port) return;
      this.log('BioSmart disconnesso dal computer');
      this._failTransport(new Error('Il dispositivo è stato disconnesso. Ricollegalo e riprova.'));
    };
  }

  static isSupported() {
    return typeof navigator !== 'undefined' && Boolean(navigator.serial);
  }

  log(message) {
    const timestamp = new Date().toLocaleTimeString();
    if (this.onLog) this.onLog(`[${timestamp}] ${message}`);
  }

  async connect() {
    if (!BIASerial.isSupported()) {
      throw new Error('Questo browser non consente il collegamento al dispositivo. Apri Sibilla con Chrome o Edge su un computer.');
    }

    if (this.connected && this.port) return;
    // A failed transport must release its old stream before a fresh connection.
    if (this._commandInFlight) throw new Error('Attendi la fine del comando prima di ricollegare BioSmart.');
    if (this.port) await this.disconnect();

    this.log('Ricerca dei dispositivi autorizzati...');
    const grantedPorts = await navigator.serial.getPorts();
    this.port = grantedPorts.length === 1
      ? grantedPorts[0]
      : await navigator.serial.requestPort();

    try {
      await this.port.open(SERIAL_OPTIONS);
    } catch (error) {
      this.port = null;
      if (error?.name === 'InvalidStateError' || error?.name === 'NetworkError') {
        throw new Error("Il dispositivo è già utilizzato da un altro programma o da un'altra scheda. Chiudili e riprova.");
      }
      throw error;
    }

    // Gedip apre la COM Bluetooth con handshake disabilitato, DTR/RTS non
    // asseriti e attende un secondo prima del primo comando. Web Serial può
    // ereditare segnali diversi dal driver: li rendiamo espliciti quando il
    // browser/driver li supporta.
    try {
      await this.port.setSignals({
        dataTerminalReady: false,
        requestToSend: false,
        break: false
      });
      this.log('Segnali seriali inizializzati: DTR off, RTS off');
    } catch (error) {
      // Alcuni driver Bluetooth SPP non espongono i segnali modem. Non è un
      // errore bloccante: Gedip usa comunque handshake=None.
      this.log(`Segnali seriali non disponibili sul driver: ${error.message}`);
    }

    try {
      const portInfo = this.port.getInfo?.() || {};
      if (portInfo.bluetoothServiceClassId) {
        this.log(`Trasporto: Bluetooth SPP (${portInfo.bluetoothServiceClassId})`);
      } else if (portInfo.usbVendorId || portInfo.usbProductId) {
        const vendor = portInfo.usbVendorId?.toString(16).padStart(4, '0') || '----';
        const product = portInfo.usbProductId?.toString(16).padStart(4, '0') || '----';
        this.log(`Trasporto: USB seriale (${vendor}:${product})`);
      } else {
        this.log('Trasporto: porta seriale/Bluetooth del sistema');
      }
    } catch {}

    await delay(1000);
    this._closing = false;
    this._readBuffer = '';
    this.writer = this.port.writable.getWriter();
    this._readTask = this._readLoop();
    navigator.serial.addEventListener('disconnect', this._disconnectHandler);
    this.connected = true;
    this.log('Collegamento locale attivo');
  }

  async _readLoop() {
    const decoder = new TextDecoder('ascii');
    this.reader = this.port.readable.getReader();

    try {
      while (!this._closing) {
        const { value, done } = await this.reader.read();
        if (done) {
          if (!this._closing) this._failTransport(new Error('La lettura dal dispositivo è terminata. Ricollega BioSmart e riprova.'));
          break;
        }
        if (!value) continue;

        this._readBuffer += decoder.decode(value, { stream: true });
        if (this._readBuffer.length > 4096) throw new Error('Risposta seriale troppo lunga. Ricollega BioSmart.');
        const lines = this._readBuffer.split(/\r\n|\r|\n/);
        this._readBuffer = lines.pop() ?? '';
        lines.map(line => line.trim()).filter(Boolean).forEach(line => this._resolveLine(line));
      }
    } catch (error) {
      if (!this._closing) {
        this._failTransport(new Error(`Comunicazione con il dispositivo interrotta: ${error.message}. Ricollega BioSmart.`));
      }
    } finally {
      try { this.reader.releaseLock(); } catch {}
      this.reader = null;
    }
  }

  _resolveLine(line) {
    this.log(`RX: ${line}`);
    if (!this._pendingLine) return;
    const pending = this._pendingLine;
    this._pendingLine = null;
    clearTimeout(pending.timer);
    pending.resolve(line);
  }

  _failTransport(error) {
    const wasConnected = this.connected;
    this.connected = false;
    this._readBuffer = '';
    this._rejectPending(error);
    if (wasConnected) this.onTransportError?.(error);
  }

  _rejectPending(error) {
    if (!this._pendingLine) return;
    const pending = this._pendingLine;
    this._pendingLine = null;
    clearTimeout(pending.timer);
    pending.reject(error);
  }

  _waitForLine(timeoutSeconds) {
    if (this._pendingLine) {
      return Promise.reject(new Error('Il dispositivo sta già elaborando un comando.'));
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!this._pendingLine) return;
        this._pendingLine = null;
        reject(new Error('Il dispositivo non ha risposto in tempo. Ricollega BioSmart prima di riprovare.'));
      }, timeoutSeconds * 1000);
      this._pendingLine = { resolve, reject, timer };
    });
  }

  async _sendCommand(command, timeoutSeconds = 7) {
    // Guard the entire write/settling/response cycle, even after a fast reply.
    if (this._commandInFlight) throw new Error('Il dispositivo sta già elaborando un comando.');
    if (!this.connected || !this.port || !this.writer) {
      throw new Error('BioSmart non collegato.');
    }

    this._commandInFlight = true;
    const response = this._waitForLine(timeoutSeconds);
    try {
      const sent = (async () => {
        this.log(`TX: ${command}`);
        await this.writer.write(new TextEncoder().encode(`${command}\r`));
        // Preserve GEDIP's one-second settling time on successful commands.
        await delay(1000);
      })();
      // Observe rejection immediately, including disconnects during write/delay.
      const [line] = await Promise.all([response, sent]);
      if (!this.connected) throw new Error('Collegamento interrotto. Ricollega BioSmart e riprova.');
      return line;
    } catch (error) {
      // Responses have no request IDs. After failure a late line cannot safely
      // be matched to another command: require an explicit reconnect.
      this._failTransport(error);
      throw error;
    } finally {
      this._commandInFlight = false;
    }
  }

  async disconnect() {
    if (!this.port) return;
    this._closing = true;
    this.connected = false;
    this._rejectPending(new Error('Dispositivo disconnesso.'));
    navigator.serial?.removeEventListener('disconnect', this._disconnectHandler);

    try { await this.reader?.cancel(); } catch {}
    try { await this._readTask; } catch {}
    try { this.writer?.releaseLock(); } catch {}
    this.writer = null;
    try { await this.port.close(); } catch {}
    this.port = null;
    this.log('Dispositivo disconnesso');
  }

  async getDeviceInfo() {
    if (this._measuring) throw new Error('Attendi la fine della misurazione prima di leggere i dati del dispositivo.');
    const raw = await this._sendCommand('[T]', 10);
    const parts = raw.split(':');
    const calibration = Number.parseFloat(parts[2]) || 0;
    const battery = Number.parseFloat(parts[3]) || 0;
    const info = {
      model: parts[0]?.trim() || '',
      serialNumber: parts[1]?.trim() || '',
      calibration,
      battery,
      firmware: parts[4]?.trim() || '',
      isCalibrated: calibration >= 1,
      isSupported: SUPPORTED_MODELS.includes(parts[0]?.trim())
    };
    this.log(`Device: ${info.model} SN:${info.serialNumber} FW:${info.firmware}`);
    return info;
  }

  async calibrate(full = false) {
    if (this._measuring) throw new Error('Attendi la fine della misurazione prima di calibrare.');
    this.log(full ? 'Calibrazione completa...' : 'Calibrazione...');
    const raw = await this._sendCommand(full ? '[CFULL]' : '[C]', full ? 130 : 80);
    const calibrated = raw.trim().toUpperCase() === 'OK';
    this.log(calibrated ? 'Calibrazione completata' : 'Calibrazione non completata');
    return calibrated;
  }

  async _measurementAt(command, frequency) {
    const raw = await this._sendCommand(command, 20);
    if (!raw || raw.trim().toUpperCase() === 'KO') {
      let deviceError = '';
      try { deviceError = await this._sendCommand('[E]', 7); } catch {}
      throw new Error(`Misura ${frequency} kHz non riuscita${deviceError ? `: ${deviceError}` : '. Verifica gli elettrodi.'}`);
    }
    return parseMeasurementLine(raw, frequency);
  }

  async measure(onProgress = () => {}, profile = DEFAULT_ACQUISITION_PROFILE) {
    if (this._measuring) throw new Error('Una misurazione è già in corso.');
    const frequencies = profileFrequencies(profile);
    this._measuring = true;
    try {
      const measurement = { acquisitionProfile: profile };
      // GedipBioImp2.SerialConnections: 5 -> 50 -> 100 kHz, pausa 250 ms.
      // Se una frequenza fallisce non restituire/salvare una misura parziale.
      for (const [index, frequency] of frequencies.entries()) {
        if (index > 0) await delay(250);
        onProgress(frequency);
        this.log(`Avvio misura a ${frequency} kHz...`);
        const value = await this._measurementAt(MEASUREMENT_COMMANDS[frequency], frequency);
        measurement[`imp_${frequency}`] = value.impedance;
        measurement[`ang_${frequency}`] = value.phase;
        measurement[`res_${frequency}`] = value.resistance;
        measurement[`rea_${frequency}`] = value.reactance;
      }
      measurement.timestamp = new Date().toISOString();
      this.log(`Acquisizione completata: ${frequencies.join(', ')} kHz`);
      return measurement;
    } finally {
      this._measuring = false;
    }
  }
}

export const SUPPORTED_BIA_MODELS = SUPPORTED_MODELS;

export default BIASerial;
